import { useMemo } from 'react'

import {
  type FunkitTheme,
  defaultDarkTheme,
  defaultLightTheme,
} from './toRestyleTheme'
import { type SystemColorScheme, useColorScheme } from './useColorScheme'

/**
 * A light/dark Restyle theme set. **At least one mode must be provided**; a
 * missing mode falls back to the one that is set. Only Restyle themes (produced
 * by `toRestyleTheme`) are accepted — raw token objects are not, so callers
 * can't accidentally pass a half-built theme.
 */
export type FunkitThemeSet =
  | { light: FunkitTheme; dark?: FunkitTheme }
  | { light?: FunkitTheme; dark: FunkitTheme }

const DEFAULT_THEME_SET: FunkitThemeSet = {
  light: defaultLightTheme,
  dark: defaultDarkTheme,
}

/**
 * Pick the Restyle theme for a color scheme, falling back to the other mode
 * when the requested one isn't in the set. The `FunkitThemeSet` union
 * guarantees at least one mode is present.
 */
export function selectTheme(
  themeSet: FunkitThemeSet,
  scheme: SystemColorScheme,
): FunkitTheme {
  const primary = scheme === 'dark' ? themeSet.dark : themeSet.light
  const fallback = scheme === 'dark' ? themeSet.light : themeSet.dark
  if (primary) {
    return primary
  }
  if (fallback) {
    return fallback
  }
  // Unreachable for typed callers; guards untyped (JS) misuse.
  throw new Error(
    'FunkitProvider: `themes` must include at least one of { light, dark }',
  )
}

/**
 * Resolves the active Restyle theme from the live system color scheme, to hand
 * to Restyle's `ThemeProvider`. Pass a custom theme set to override the
 * built-in Fun Mobile themes. Falls back to light when the system scheme is
 * unknown.
 */
export function useFunkitTheme(
  themeSet: FunkitThemeSet = DEFAULT_THEME_SET,
): FunkitTheme {
  const scheme = useColorScheme()
  // Depend on the individual theme refs, not the `themeSet` object — an inline
  // set is a new object each render and would defeat the memo.
  return useMemo(
    () => selectTheme(themeSet, scheme),
    [scheme, themeSet.light, themeSet.dark],
  )
}
